import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import apiClient from '../services/apiClient';
import {
  Upload, ArrowLeft, Loader2, Sparkles, Image as ImageIcon,
  Check, X, RefreshCw, Download, Eye, Edit3,
  Settings, LogOut, TrendingUp, Layers,
  ChevronRight, Zap, Star, Shield, Camera, Box, Target,
  AlertCircle, Clipboard, Plus, Trash2, Ruler, Cpu, ListOrdered,
  Search, FileText, Clock, FolderOpen, Package,
  BookOpen, Palette, Type
} from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import SeoOptimizerTab from '../components/publications/SeoOptimizerTab';
import DescriptionGeneratorTab from '../components/publications/DescriptionGeneratorTab';
import CreditsBadge from '../components/CreditsBadge';
import BuyCreditsModal from '../components/BuyCreditsModal';
import TutorialVideoButton from '../components/TutorialVideoButton';

const MAX_PRODUCT_IMAGES = 5; // keep in sync with backend/api/v1/publications.py

const STEPS = [
  { id: 'upload', label: 'Subir Producto', icon: Upload },
  { id: 'analyze', label: 'Análisis IA', icon: Sparkles },
  { id: 'generate', label: 'Generar Infografías', icon: ImageIcon },
  { id: 'preview', label: 'Resultado', icon: Eye },
];

// 7 Infografías de Alto Impacto (usuario sube su propia portada)
const SEQUENCE_INFO = [
  { pos: 1, name: 'Infografía 1', icon: Zap, color: 'from-emerald-500 to-green-600', desc: 'Layout Multi-Escenarios' },
  { pos: 2, name: 'Infografía 2', icon: Camera, color: 'from-blue-500 to-cyan-600', desc: 'Layout Anatomía Conectada' },
  { pos: 3, name: 'Infografía 3', icon: Shield, color: 'from-cyan-500 to-cyan-600', desc: 'Layout Tabla Rendimiento' },
  { pos: 4, name: 'Comparativa', icon: Target, color: 'from-rose-500 to-pink-600', desc: 'Nosotros vs Competencia' },
  { pos: 5, name: 'Dimensiones', icon: Ruler, color: 'from-teal-500 to-emerald-600', desc: 'Medidas y escala real' },
  { pos: 6, name: 'Infografía 6', icon: Box, color: 'from-orange-500 to-amber-600', desc: 'Layout adicional' },
  { pos: 7, name: 'Infografía 7', icon: Sparkles, color: 'from-blue-500 to-cyan-600', desc: 'Layout adicional' },
];

// All 7 positions are active
const ACTIVE_SEQUENCE = SEQUENCE_INFO;
const ACTIVE_POSITIONS = [1, 2, 3, 4, 5, 6, 7];

const Publications = () => {
  // Polling interval for job status updates (milliseconds)
  const POLL_INTERVAL = 15000;
  const POLL_ERROR_INTERVAL = 5000;

  const { user, logout, loading: authLoading } = useAuth();
  const { fetchWithCache, invalidateCache } = useDataCache();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Tab activo - leer de URL si viene con ?tab=seo o ?tab=description
  const tabParam = searchParams.get('tab');
  const initialTab = tabParam === 'seo' ? 'seo' : tabParam === 'description' ? 'description' : 'generator';
  const [activeTab, setActiveTab] = useState(initialTab);

  const [step, setStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [currentGenPos, setCurrentGenPos] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const [sessionId, setSessionId] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [productPreviews, setProductPreviews] = useState([]); // Support multiple images
  const [initialPrompt, setInitialPrompt] = useState(''); // Optional product info
  const [analysis, setAnalysis] = useState(null);
  const [originalAnalysis, setOriginalAnalysis] = useState(null); // Para restaurar al cancelar
  const [editingAnalysis, setEditingAnalysis] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);

  // ══════════════════════════════════════════════════════════════════════════
  // HISTORIAL DE GENERACIONES (24 horas)
  // ══════════════════════════════════════════════════════════════════════════
  const [generationHistory, setGenerationHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPreview, setHistoryPreview] = useState(null); // Para preview de sesión del historial

  // ══════════════════════════════════════════════════════════════════════════
  // ESTADO COMPARTIDO SEO → DESCRIPCIÓN (en memoria de sesión)
  // ══════════════════════════════════════════════════════════════════════════
  const [seoContext, setSeoContext] = useState(null);
  // Estructura de seoContext:
  // {
  //   productName: string,
  //   keywords: string[],
  //   titles: string[],
  //   category: string,
  //   attributes: object,
  //   timestamp: Date
  // }

  const [brandProfiles, setBrandProfiles] = useState([]);
  const [brandUploading, setBrandUploading] = useState(false);

  const fileInputRef = useRef(null);
  const brandFileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !user) navigate('/login');
  }, [user, authLoading, navigate]);

  const fetchBrandProfiles = useCallback((forceRefresh = false) => {
    return fetchWithCache(
      'publications_brand-profile',
      async () => {
        const { data } = await apiClient.get('/api/publications/brand-profile');
        return data.profiles || [];
      },
      forceRefresh
    ).then(setBrandProfiles).catch(() => {});
  }, [fetchWithCache]);

  useEffect(() => {
    if (!user) return;
    fetchBrandProfiles();
    // Depend on user?.id, not the user object: AuthContext replaces `user` with a new
    // object reference (same data) once its background token verification resolves,
    // which would otherwise re-run this effect and double-fetch.
  }, [user?.id, fetchBrandProfiles]); // eslint-disable-line react-hooks/exhaustive-deps

  const [imageCredits, setImageCredits] = useState(null);
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);
  const fetchImageCredits = useCallback((forceRefresh = false) => {
    return fetchWithCache(
      'publications_image-credits',
      async () => {
        const { data } = await apiClient.get('/api/publications/credits');
        return data;
      },
      forceRefresh
    ).then(setImageCredits).catch(() => {});
  }, [fetchWithCache]);

  useEffect(() => {
    if (!user) return;
    // Coming back from a Stripe credit-pack checkout (see CreditsReturn.jsx)
    // — bypass the 10-minute cache so the badge reflects the new balance.
    fetchImageCredits(Boolean(location.state?.forceRefreshCredits));
  }, [user?.id, fetchImageCredits]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBrandUpload = async (file) => {
    if (!file) return;
    setBrandUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Dispatches an async extract job in ai-worker instead of extracting
      // inline — poll .../brand-profile-status/{job_id} for the result.
      const { data: queued } = await apiClient.post('/api/publications/brand-profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });

      toast.loading('Analizando manual de marca...', { id: 'brand-upload' });

      const maxAttempts = 60; // 60 * 3s = 3 minutos máximo
      let attempts = 0;
      let finished = false;

      while (attempts < maxAttempts && !finished) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL));
        try {
          const { data: statusData } = await apiClient.get(
            `/api/publications/brand-profile-status/${queued.job_id}`
          );

          if (statusData.status === 'completed' && statusData.result) {
            const data = statusData.result;
            setBrandProfiles(prev => {
              const deactivated = data.is_active ? prev.map(p => ({ ...p, is_active: false })) : [...prev];
              return [{ profile_id: data.profile_id, name: data.name, profile: data.profile, is_active: data.is_active }, ...deactivated];
            });
            invalidateCache('publications_brand-profile');
            toast.success(`Marca "${data.name}" guardada${data.is_active ? ' y activada' : ''}`, { id: 'brand-upload' });
            finished = true;
          } else if (statusData.status === 'error') {
            toast.error('Error al procesar: ' + (statusData.error || 'Error desconocido'), { id: 'brand-upload' });
            finished = true;
          }
        } catch (err) {
          console.error('Error polling brand-profile status:', err);
        }
        attempts++;
      }

      if (!finished) {
        toast.error('Timeout analizando el manual de marca', { id: 'brand-upload' });
      }
    } catch (err) {
      toast.error('Error al procesar: ' + (err.response?.data?.detail || err.message), { id: 'brand-upload' });
    } finally {
      setBrandUploading(false);
      if (brandFileInputRef.current) brandFileInputRef.current.value = '';
    }
  };

  const handleBrandActivate = async (profileId) => {
    const isActive = brandProfiles.find(p => p.profile_id === profileId)?.is_active;
    try {
      if (isActive) {
        await apiClient.delete('/api/publications/brand-profile/deactivate-all');
        setBrandProfiles(prev => prev.map(p => ({ ...p, is_active: false })));
        toast.success('Marca desactivada');
      } else {
        await apiClient.put(`/api/publications/brand-profile/${profileId}/activate`);
        setBrandProfiles(prev => prev.map(p => ({ ...p, is_active: p.profile_id === profileId })));
        toast.success('Marca activada');
      }
      invalidateCache('publications_brand-profile');
    } catch {
      toast.error('Error al cambiar estado de la marca');
    }
  };

  const handleBrandDelete = async (profileId) => {
    try {
      await apiClient.delete(`/api/publications/brand-profile/${profileId}`);
      setBrandProfiles(prev => prev.filter(p => p.profile_id !== profileId));
      invalidateCache('publications_brand-profile');
      toast.success('Perfil eliminado');
    } catch {
      toast.error('Error al eliminar perfil de marca');
    }
  };

  // Global paste handler
  useEffect(() => {
    if (step !== 0) return;
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) processFile(file);
          return;
        }
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [step, uploading]);

  const handleLogout = () => { logout(); navigate('/'); };

  // Core file processing - only builds local previews, does NOT upload yet
  const processFiles = async (files) => {
    if (!files || files.length === 0) return;

    // Filter valid images
    const validFiles = Array.from(files).filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} no es una imagen válida`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} es demasiado grande (máx 10MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    const existingCount = sessionId ? productPreviews.length : selectedFiles.length;
    const remainingSlots = MAX_PRODUCT_IMAGES - existingCount;

    if (remainingSlots <= 0) {
      toast.error(`Ya subiste el máximo de ${MAX_PRODUCT_IMAGES} fotos`);
      return;
    }

    if (validFiles.length > remainingSlots) {
      toast.error(`Máximo ${MAX_PRODUCT_IMAGES} fotos. Se tomaron las primeras ${remainingSlots}.`);
    }

    const filesToAdd = validFiles.slice(0, remainingSlots);

    // If session already exists (step 1+), add to it directly
    if (sessionId) {
      for (const file of filesToAdd) {
        await addImageToSession(file);
      }
      return;
    }

    // No session yet — accumulate files and show previews locally
    const newFiles = [...selectedFiles, ...filesToAdd];
    setSelectedFiles(newFiles);

    const previews = [];
    for (const file of newFiles) {
      const preview = await new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.readAsDataURL(file);
      });
      previews.push(preview);
    }
    setProductPreviews(previews);
  };

  // Uploads files and starts AI analysis — triggered manually by the user
  const handleStartAnalysis = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Sube al menos una imagen del producto');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => formData.append('files', file));
      if (initialPrompt.trim()) {
        formData.append('initial_prompt', initialPrompt.trim());
      }

      const { data } = await apiClient.post('/api/publications/upload-product-multi', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });

      // El backend descontó un crédito de generación al aceptar el análisis
      fetchImageCredits(true);

      setSessionId(data.session_id);
      toast.success(`${selectedFiles.length} imagen(es) subidas. Analizando...`);

      await pollAnalysisStatus(data.session_id);

    } catch (err) {
      if (err.response?.status === 429) {
        const message = err.response?.data?.detail || 'Alcanzaste el límite de generaciones de tu plan.';
        toast.error(message, {
          duration: 8000,
          action: { label: 'Comprar créditos', onClick: () => setBuyCreditsOpen(true) },
        });
        fetchImageCredits(true);
      } else {
        toast.error('Error al subir: ' + (err.response?.data?.detail || err.message));
      }
      setProductPreviews([]);
      setSelectedFiles([]);
      setUploading(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // POLLING DEL ANÁLISIS DEL DIRECTOR DE ARTE
  // ══════════════════════════════════════════════════════════════════════════
  // Shared by every flow that dispatches an analyze_plan job to ai-worker
  // (initial upload, add-image, and analysis edits): polls until
  // analysis_status is 'completed'/'failed', or returns null on timeout.
  const waitForAnalysis = async (sid, maxAttempts = 60) => {
    let attempts = 0;
    while (attempts < maxAttempts) {
      try {
        const { data } = await apiClient.get(`/api/publications/session/${sid}/analysis-status`);
        if (data.analysis_status === 'completed' || data.analysis_status === 'failed') {
          return data;
        }
        await new Promise(r => setTimeout(r, POLL_INTERVAL));
        attempts++;
      } catch (err) {
        console.error('Error polling analysis:', err);
        await new Promise(r => setTimeout(r, POLL_ERROR_INTERVAL));
        attempts++;
      }
    }
    return null;
  };

  const pollAnalysisStatus = async (sid) => {
    const data = await waitForAnalysis(sid);

    if (data?.analysis_status === 'completed') {
      setAnalysis(data.analysis);
      setStep(1);
      setUploading(false);
      toast.success('Análisis completado');
      return;
    }

    if (data?.analysis_status === 'failed') {
      // Usar análisis fallback
      setAnalysis(data.analysis);
      setStep(1);
      setUploading(false);
      toast.warning('Análisis con datos básicos (hubo un error con la IA)');
      return;
    }

    // Timeout - usar datos básicos
    setAnalysis({
      nombre_producto: "Producto",
      categoria: "General",
      caracteristicas: [],
      beneficios: [],
    });
    setStep(1);
    setUploading(false);
    toast.error('Timeout en análisis. Puedes continuar con datos básicos.');
  };

  // Single file processing (for backwards compatibility with paste)
  const processFile = async (file) => {
    await processFiles([file]);
  };

  // Add additional image to existing session
  const addImageToSession = async (file) => {
    if (!sessionId) return;
    if (productPreviews.length >= MAX_PRODUCT_IMAGES) {
      toast.error(`Ya subiste el máximo de ${MAX_PRODUCT_IMAGES} fotos`);
      return;
    }
    
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = (ev) => setProductPreviews(prev => [...prev, ev.target.result]);
      reader.readAsDataURL(file);

      const formData = new FormData();
      formData.append('file', file);
      await apiClient.post(`/api/publications/session/${sessionId}/add-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      toast.success(`Imagen ${productPreviews.length + 1} añadida. Re-analizando...`);

      // add-image dispatches an async analyze_plan job in ai-worker instead
      // of returning the fresh plan inline — poll the same way the initial
      // upload does. pollAnalysisStatus clears `uploading` itself.
      await pollAnalysisStatus(sessionId);
    } catch (err) {
      toast.error('Error al añadir imagen: ' + (err.response?.data?.detail || err.message));
      setUploading(false);
    }
  };

  // File input handler - now supports multiple files
  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
  };

  // Clear the locally-selected images (step 0, before analysis starts —
  // nothing has been uploaded to the backend yet, so this is a pure
  // client-side reset).
  const handleClearSelection = () => {
    setSelectedFiles([]);
    setProductPreviews([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Drag & drop handlers - now supports multiple files
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
  };

  // Analysis
  const handleSaveAnalysis = async () => {
    if (!sessionId) return;
    try {
      console.log('Saving analysis:', JSON.stringify(analysis).slice(0, 200));
      // PUT now just dispatches an async analyze_plan job in ai-worker (it
      // regenerates the image prompts around the edited fields) — poll for
      // it to actually finish instead of treating the PUT itself as done.
      await apiClient.put(`/api/publications/session/${sessionId}/analysis`, { analysis });
      toast.loading('Actualizando análisis con tus cambios...', { id: 'save-analysis' });

      const data = await waitForAnalysis(sessionId);
      toast.dismiss('save-analysis');

      if (data?.analysis_status === 'completed') {
        setAnalysis(data.analysis);
        setOriginalAnalysis(null); // Limpiar el backup después de guardar
        setEditingAnalysis(false);
        toast.success('Análisis guardado correctamente');
      } else if (data?.analysis_status === 'failed') {
        toast.error('Error al regenerar el análisis: ' + (data.analysis_error || 'Error desconocido'));
      } else {
        toast.error('Timeout esperando la actualización del análisis');
      }
    } catch (err) {
      console.error('Error saving analysis:', err);
      toast.error('Error al guardar: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Generate all with async polling - Production-ready approach
  // Este método evita timeouts en producción usando polling del estado del job
  const handleGenerateAll = async () => {
    if (!sessionId) return;
    setGenerating(true);
    setCurrentGenPos(1);
    setStep(2);
    setGeneratedImages([]);

    // Función helper para reintentar requests
    const retryRequest = async (requestFn, maxRetries = 2) => {
      let lastError;
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          return await requestFn();
        } catch (err) {
          lastError = err;
          console.log(`Request failed (attempt ${attempt + 1}/${maxRetries + 1}):`, err.message);
          if (attempt < maxRetries) {
            await new Promise(r => setTimeout(r, 1500)); // Esperar 1.5s antes de reintentar
          }
        }
      }
      throw lastError;
    };

    try {
      // PASO 1: Enviar el análisis editado al backend ANTES de generar
      // Esto asegura que cualquier cambio del usuario se tome en cuenta.
      // El PUT solo despacha un job async en ai-worker — hay que esperar a
      // que termine para que generate-all arranque con el plan ya actualizado.
      if (analysis) {
        console.log('Syncing edited analysis before generation...');
        await retryRequest(() => apiClient.put(
          `/api/publications/session/${sessionId}/analysis`,
          { analysis },
          { timeout: 45000 }  // Aumentado a 45s
        ));
        const analysisResult = await waitForAnalysis(sessionId);
        if (!analysisResult || analysisResult.analysis_status === 'failed') {
          console.warn('Analysis re-sync did not complete cleanly, proceeding with existing plan');
        }
      }

      // PASO 2: Iniciar la generación asíncrona (con retry)
      const { data: startData } = await retryRequest(() => apiClient.post(
        `/api/publications/session/${sessionId}/generate-all`, {},
        { timeout: 45000 }  // Aumentado a 45s
      ));
      
      if (startData.status === 'already_running') {
        toast.info('Ya hay una generación en progreso');
      }

      const jobId = startData.job_id;
      console.log('Generation job started:', jobId);
      
      // Polling del estado cada 3 segundos
      let attempts = 0;
      const maxAttempts = 200; // ~10 minutos máximo
      
      const pollStatus = async () => {
        try {
          const { data: statusData } = await apiClient.get(
            `/api/publications/session/${sessionId}/job-status`
          );
          
          // Actualizar posición actual para la UI
          if (statusData.current_position) {
            setCurrentGenPos(statusData.current_position);
          }
          
          // Actualizar imágenes generadas hasta ahora
          if (statusData.results && statusData.results.length > 0) {
            setGeneratedImages(statusData.results.sort((a, b) => a.position - b.position));
          }
          
          // Verificar si el job terminó
          if (statusData.status === 'completed') {
            setGenerating(false);
            setCurrentGenPos(0);
            setStep(3);
            const successCount = (statusData.results || []).filter(r => r.success).length;
            toast.success(`¡${successCount} infografías generadas con éxito!`);
            return true;
          }
          
          if (statusData.status === 'failed') {
            setGenerating(false);
            setCurrentGenPos(0);
            
            // Verificar si el error es por IA no disponible
            const results = statusData.results || [];
            const aiUnavailable = results.some(r => r.error_type === 'ai_unavailable' || 
              (r.error && r.error.includes('IA no disponible')));
            
            if (aiUnavailable) {
              toast.error('IA no disponible en este momento. Por favor, intenta de nuevo en unos minutos.', { 
                duration: 8000,
                icon: '🔴'
              });
            } else {
              toast.error(`Error en generación: ${statusData.error || 'Error desconocido'}`);
            }
            
            if (results.length > 0) {
              setStep(3); // Mostrar resultados parciales
            } else {
              setStep(1); // Volver a análisis
            }
            return true;
          }
          
          return false; // Continuar polling
          
        } catch (err) {
          console.error('Polling error:', err);
          return false; // Continuar intentando
        }
      };
      
      // Loop de polling
      while (attempts < maxAttempts) {
        const done = await pollStatus();
        if (done) break;
        
        attempts++;
        await new Promise(r => setTimeout(r, 3000)); // Esperar 3 segundos
      }
      
      if (attempts >= maxAttempts) {
        toast.error('La generación está tardando demasiado. Revisa el estado más tarde.');
        setGenerating(false);
      }
      
    } catch (err) {
      console.error('Generation error:', err);
      toast.error('Error al iniciar la generación');
      setGenerating(false);
      setCurrentGenPos(0);
      setStep(1);
    }
  };

  // Regenerate single
  const handleRegenerate = async (pos) => {
    if (!sessionId) return;
    setCurrentGenPos(pos);
    try {
      // Dispatches an async regenerate_position job in ai-worker instead of
      // generating inline — poll .../regen-status the same way generate-all
      // polls .../job-status.
      await apiClient.post(
        `/api/publications/session/${sessionId}/generate/${pos}`, {},
        { timeout: 30000 }
      );

      const maxAttempts = 60; // 60 * 3s = 3 minutos máximo
      let attempts = 0;
      let finished = false;

      while (attempts < maxAttempts && !finished) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL));
        try {
          const { data: statusData } = await apiClient.get(
            `/api/publications/session/${sessionId}/regen-status`
          );

          if (statusData.status === 'completed' && statusData.result) {
            setGeneratedImages(prev => {
              const updated = [...prev.filter(g => g.position !== pos), statusData.result];
              updated.sort((a, b) => a.position - b.position);
              return updated;
            });
            toast.success(`Infografía ${pos} regenerada`);
            finished = true;
          } else if (statusData.status === 'failed') {
            toast.error('Error al regenerar: ' + (statusData.error || 'Error desconocido'));
            finished = true;
          }
        } catch (err) {
          console.error('Error polling regen status:', err);
        }
        attempts++;
      }

      if (!finished) {
        toast.error('Timeout regenerando la infografía');
      }
    } catch (err) {
      toast.error('Error al regenerar');
    }
    finally { setCurrentGenPos(0); }
  };

  // Image URL (for <img src>). `version` should be something that changes
  // whenever the underlying image changes (e.g. generated_at) — without it,
  // the URL is identical before/after a regeneration and the browser just
  // serves its cached copy instead of refetching the new image.
  const getImageUrl = (pos, version) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('authToken');
    const versionParam = version ? `&v=${encodeURIComponent(version)}` : '';
    return `${baseUrl}/api/publications/session/${sessionId}/image/${pos}?token=${token}${versionParam}`;
  };

  // ══════════════════════════════════════════════════════════════════════════
  // HISTORIAL - Cargar generaciones anteriores (24 horas)
  // ══════════════════════════════════════════════════════════════════════════
  const loadGenerationHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await apiClient.get('/api/publications/history');
      setGenerationHistory(response.data.history || []);
    } catch (err) {
      console.error('Error loading history:', err);
      toast.error('Error al cargar historial');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Cargar preview de una sesión del historial
  const loadHistoryPreview = async (historySessionId) => {
    try {
      const response = await apiClient.get(`/api/publications/session/${historySessionId}/images-preview`);
      setHistoryPreview(response.data);
    } catch (err) {
      console.error('Error loading preview:', err);
      toast.error('Error al cargar vista previa');
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // DESCARGA ZIP - Opción principal con fallback
  // ══════════════════════════════════════════════════════════════════════════
  const handleDownloadZip = async (targetSessionId = sessionId) => {
    try {
      toast.loading('Preparando descarga ZIP...', { id: 'zip-download' });
      
      const response = await apiClient.get(
        `/api/publications/session/${targetSessionId}/download-zip`,
        { responseType: 'blob' }
      );
      
      // Obtener nombre del archivo del header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'imagenes.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=([^;]+)/);
        if (match) filename = match[1].trim();
      }
      
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('ZIP descargado correctamente', { id: 'zip-download' });
    } catch (err) {
      console.error('ZIP download error:', err);
      toast.error('Error al descargar ZIP. Usa los enlaces individuales como alternativa.', { id: 'zip-download' });
    }
  };

  // Descarga individual con fallback (enlace directo)
  const handleDownloadDirect = async (pos, targetSessionId = sessionId) => {
    try {
      const response = await apiClient.get(
        `/api/publications/session/${targetSessionId}/image/${pos}/direct`,
        { responseType: 'blob' }
      );
      
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `infografia_${pos}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Imagen ${pos} descargada`);
    } catch (err) {
      console.error('Direct download error:', err);
      toast.error(`Error al descargar imagen ${pos}`);
    }
  };

  // Download (uses apiClient with Authorization header) - Mantener para compatibilidad
  const handleDownload = async (pos) => {
    try {
      const response = await apiClient.get(
        `/api/publications/session/${sessionId}/image/${pos}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `imagen_${pos}_${SEQUENCE_INFO[pos - 1].name.toLowerCase().replace(/\s/g, '_')}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Imagen descargada');
    } catch (err) { 
      console.error('Download error:', err);
      // Fallback a descarga directa
      toast.info('Intentando descarga alternativa...');
      await handleDownloadDirect(pos);
    }
  };

  // Download all - Ahora usa ZIP como opción principal
  const handleDownloadAll = async () => {
    // Intentar ZIP primero
    await handleDownloadZip();
  };

  // Reset
  const handleReset = () => {
    setStep(0);
    setSessionId(null);
    setSelectedFiles([]);
    setProductPreviews([]);
    setInitialPrompt('');
    setAnalysis(null);
    setGeneratedImages([]);
    setEditingAnalysis(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (authLoading || !user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="publications-page">
      {/* Always-mounted file input so fileInputRef works across all steps */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileInput}
        disabled={uploading}
        data-testid="upload-input"
      />
      <input
        ref={brandFileInputRef}
        type="file"
        accept=".pdf,image/*"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && handleBrandUpload(e.target.files[0])}
        data-testid="brand-upload-input"
      />
      {/* Nav */}
      <nav className="bg-card/60 backdrop-blur-xl border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/dashboard')} data-testid="nav-dashboard" className="text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold font-outfit text-foreground">Construcción de Publicaciones</h1>
            </div>
            <div className="flex items-center gap-4">
              <TutorialVideoButton video="Publicaciones.mp4" caption="Cómo generar publicaciones con IA." />
              <button onClick={() => navigate('/dashboard')} data-testid="nav-back-dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <Layers className="w-5 h-5" />
                <span className="hidden md:inline">Dashboard</span>
              </button>
              <button onClick={() => navigate('/profitability')} data-testid="nav-profitability" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <TrendingUp className="w-5 h-5" />
                <span className="hidden md:inline">Rentabilidad</span>
              </button>
              <div className="border-l border-border pl-4 ml-2">
                <button onClick={handleLogout} data-testid="logout-button" className="text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400 transition-colors">
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-6 py-8 max-w-6xl">
        {/* Tabs principales */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-xl mx-auto grid-cols-3 mb-8 bg-muted p-1">
            <TabsTrigger 
              value="generator" 
              className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm"
              data-testid="tab-generator"
            >
              <ImageIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Generador Imágenes</span>
              <span className="sm:hidden">Imágenes</span>
            </TabsTrigger>
            <TabsTrigger 
              value="seo" 
              className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm"
              data-testid="tab-seo"
            >
              <Search className="w-4 h-4" />
              <span className="hidden sm:inline">Optimizador SEO</span>
              <span className="sm:hidden">SEO</span>
            </TabsTrigger>
            <TabsTrigger 
              value="description" 
              className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm"
              data-testid="tab-description"
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Descripción</span>
              <span className="sm:hidden">Descripción</span>
            </TabsTrigger>
          </TabsList>

          {/* Tab: Generador de Imágenes */}
          <TabsContent value="generator" className="mt-0">
            {/* Step indicator */}
            <div className="flex items-center justify-center gap-2 mb-10" data-testid="step-indicator">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const isActive = i === step;
                const isDone = i < step;
                return (
                  <React.Fragment key={s.id}>
                    {i > 0 && <div className={`w-12 h-0.5 ${isDone ? 'bg-blue-500' : 'bg-slate-700'}`} />}
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all
                      ${isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' : ''}
                      ${isDone ? 'bg-blue-600/20 text-blue-600 dark:text-blue-400' : ''}
                      ${!isActive && !isDone ? 'bg-muted text-muted-foreground/70' : ''}
                    `}>
                      {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                      <span className="hidden sm:inline">{s.label}</span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Always visible across every step of this module, not just at upload time */}
            <div className="flex justify-center mb-8">
              <CreditsBadge
                credits={imageCredits}
                label="Generaciones"
                data-testid="image-credits-badge-header"
                onBuyClick={() => setBuyCreditsOpen(true)}
              />
            </div>

        {/* ── Step 0: Upload ── */}
        {step === 0 && (
          <div className="space-y-8" data-testid="step-upload">
            <div className="text-center space-y-3">
              <h2 className="text-3xl font-bold font-outfit text-foreground">Generador de Imágenes para MeLi</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Sube una o varias fotos de tu producto y generaremos <span className="text-blue-600 dark:text-blue-400 font-semibold">7 infografías profesionales</span> con textos optimizados para maximizar conversiones en Mercado Libre.
              </p>
            </div>

            {/* Upload area with drag & drop */}
            <div className="max-w-xl mx-auto">
              {productPreviews.length > 0 && !uploading && (
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">
                    {productPreviews.length} de {MAX_PRODUCT_IMAGES} fotos seleccionadas
                  </span>
                  <button
                    type="button"
                    onClick={handleClearSelection}
                    data-testid="clear-selection-btn"
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-red-500 dark:hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Limpiar selección
                  </button>
                </div>
              )}
              <div
                ref={dropZoneRef}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !uploading && fileInputRef.current?.click()}
                className={`flex flex-col items-center justify-center w-full min-h-72 border-2 border-dashed rounded-2xl cursor-pointer transition-all
                  ${dragOver
                    ? 'border-blue-400 bg-blue-500/10 scale-[1.02]'
                    : 'border-border bg-popover/30 hover:bg-popover/60 hover:border-blue-500/50'}
                  ${uploading ? 'pointer-events-none opacity-70' : ''}
                `}
                data-testid="upload-dropzone"
              >
                {productPreviews.length > 0 && !uploading ? (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 p-4 w-full">
                    {productPreviews.map((preview, i) => (
                      <img key={i} src={preview} alt={`Preview ${i + 1}`} className="w-full aspect-square object-cover rounded-lg" />
                    ))}
                    {productPreviews.length < MAX_PRODUCT_IMAGES && (
                      <div className="w-full aspect-square flex items-center justify-center border-2 border-dashed border-border rounded-lg text-muted-foreground/70 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                        <Plus className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-4 text-muted-foreground">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors
                      ${dragOver ? 'bg-blue-600/30 text-blue-600 dark:text-blue-400' : 'bg-muted'}`}>
                      <Upload className="w-8 h-8" />
                    </div>
                    <div className="text-center">
                      <p className="font-medium text-foreground">
                        {dragOver ? 'Suelta la imagen aquí' : 'Arrastra, pega o haz clic'}
                      </p>
                      <p className="text-sm mt-1">PNG, JPG o WEBP — Máximo 10MB</p>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">Sube hasta {MAX_PRODUCT_IMAGES} fotos para mayor variedad</p>
                      <div className="flex items-center justify-center gap-2 mt-3 text-xs text-muted-foreground/70">
                        <Clipboard className="w-3.5 h-3.5" />
                        <span>También puedes pegar con Ctrl+V</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Optional initial prompt */}
              <div className="mt-4">
                <label className="text-xs font-medium text-muted-foreground block mb-1.5">
                  Información adicional del producto (opcional)
                </label>
                <textarea
                  value={initialPrompt}
                  onChange={(e) => setInitialPrompt(e.target.value)}
                  placeholder="Ej: Auriculares Bluetooth con cancelación de ruido, 40 horas de batería, resistentes al agua IPX5. Beneficios: comodidad extrema, sonido Hi-Fi..."
                  className="w-full px-4 py-3 rounded-xl bg-card/60 border border-border text-foreground text-sm placeholder-muted-foreground/70 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none"
                  rows={3}
                  disabled={uploading}
                  data-testid="initial-prompt-input"
                />
                <p className="text-xs text-muted-foreground/70 mt-1">Describe tu producto, beneficios, características o instrucciones especiales para las imágenes</p>
              </div>

              {/* Analizar button — shown once at least one image is selected */}
              {selectedFiles.length > 0 && !uploading && (
                <div className="mt-6 flex flex-col items-center gap-3">
                  <button
                    onClick={handleStartAnalysis}
                    disabled={imageCredits && !imageCredits.unlimited && imageCredits.remaining <= 0 && (imageCredits.purchased_balance || 0) <= 0}
                    className="flex items-center gap-3 px-8 py-4 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold text-lg hover:from-blue-500 hover:to-cyan-500 shadow-lg shadow-blue-500/30 transition-all transform hover:-translate-y-0.5
                      disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    data-testid="start-analysis-btn"
                  >
                    <Sparkles className="w-5 h-5" />
                    Analizar Producto ({selectedFiles.length} {selectedFiles.length === 1 ? 'imagen' : 'imágenes'})
                  </button>
                </div>
              )}

              {uploading && (
                <div className="mt-6 p-4 bg-popover/70 border border-blue-500/30 rounded-xl">
                  <div className="flex items-center justify-center gap-3 text-blue-600 dark:text-blue-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="font-medium">
                      {sessionId ? 'Analizando producto con IA...' : 'Subiendo y removiendo fondo...'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground/70 text-center mt-2">
                    {sessionId 
                      ? 'El Director de Arte está analizando tu producto. Esto puede tomar hasta 2 minutos.' 
                      : 'Procesando imagen con Photoroom...'}
                  </p>
                </div>
              )}
            </div>

            {/* ── Manual de Marca ── */}
            {(() => {
              const activeProfile = brandProfiles.find(p => p.is_active);
              return (
                <div className="max-w-xl mx-auto mt-8">
                  <div className="rounded-2xl border border-border bg-popover/40 p-5">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                        <span className="font-semibold text-foreground text-sm">Manual de Marca</span>
                        {activeProfile && (
                          <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 text-xs font-medium">Activo</span>
                        )}
                      </div>
                      <button
                        onClick={() => brandFileInputRef.current?.click()}
                        disabled={brandUploading}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted hover:bg-slate-700 text-muted-foreground hover:text-foreground transition-all text-xs font-medium disabled:opacity-50"
                        data-testid="brand-upload-btn"
                      >
                        {brandUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        Subir marca
                      </button>
                    </div>

                    {brandUploading && (
                      <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-sm py-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Analizando manual con IA...</span>
                      </div>
                    )}

                    {!brandUploading && brandProfiles.length === 0 && (
                      <p className="text-muted-foreground/70 text-xs leading-relaxed">
                        Sube tu manual de marca (PDF o imagen) para que las infografías respeten tu identidad visual: colores, tipografía y tono.
                      </p>
                    )}

                    {/* Profile list */}
                    {!brandUploading && brandProfiles.length > 0 && (
                      <div className="space-y-2">
                        {brandProfiles.map(p => (
                          <div
                            key={p.profile_id}
                            onClick={() => handleBrandActivate(p.profile_id)}
                            className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all
                              ${p.is_active
                                ? 'border-amber-500/50 bg-amber-500/5'
                                : 'border-border hover:border-border hover:bg-muted/30'}`}
                            data-testid={`brand-profile-${p.profile_id}`}
                          >
                            {/* Color swatch */}
                            <div
                              className="w-8 h-8 rounded-full flex-shrink-0 border-2 border-white/10"
                              style={{ backgroundColor: p.profile?.colores?.primario || '#6366f1' }}
                            />
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{p.name}</p>
                              {p.profile?.tono_visual && (
                                <p className="text-xs text-muted-foreground/70 truncate">{p.profile.tono_visual}</p>
                              )}
                            </div>
                            {/* Radio indicator */}
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors
                              ${p.is_active ? 'border-amber-400' : 'border-border'}`}>
                              {p.is_active && <div className="w-2 h-2 rounded-full bg-amber-400" />}
                            </div>
                            {/* Delete */}
                            <button
                              onClick={e => { e.stopPropagation(); handleBrandDelete(p.profile_id); }}
                              className="p-1.5 rounded-lg hover:bg-rose-500/20 text-muted-foreground/70 hover:text-rose-600 dark:hover:text-rose-400 transition-all flex-shrink-0"
                              data-testid={`brand-delete-${p.profile_id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Active profile detail */}
                    {activeProfile?.profile && !brandUploading && (
                      <div className="mt-4 pt-4 border-t border-border/60 space-y-3">
                        {activeProfile.profile.colores && (
                          <div className="flex flex-wrap gap-2">
                            {[
                              { label: 'Primario', hex: activeProfile.profile.colores.primario },
                              { label: 'Acento', hex: activeProfile.profile.colores.acento },
                              { label: 'Fondo', hex: activeProfile.profile.colores.neutro_fondo },
                              ...(activeProfile.profile.colores.otros || []).map((h, i) => ({ label: `Extra ${i + 1}`, hex: h })),
                            ].filter(c => c.hex).map(c => (
                              <div key={c.label} className="flex items-center gap-1.5 bg-muted rounded-lg px-2.5 py-1.5">
                                <div className="w-3.5 h-3.5 rounded-full border border-white/20 flex-shrink-0" style={{ backgroundColor: c.hex }} />
                                <div>
                                  <p className="text-xs text-muted-foreground font-medium leading-none">{c.label}</p>
                                  <p className="text-[10px] text-muted-foreground/70 font-mono">{c.hex}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          {(activeProfile.profile.tipografia?.titular || activeProfile.profile.tipografia?.cuerpo) && (
                            <div className="bg-muted rounded-lg p-2.5">
                              <div className="flex items-center gap-1 mb-1">
                                <Type className="w-3 h-3 text-muted-foreground/70" />
                                <span className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">Tipografía</span>
                              </div>
                              {activeProfile.profile.tipografia.titular && (
                                <p className="text-xs text-muted-foreground truncate">{activeProfile.profile.tipografia.titular}</p>
                              )}
                            </div>
                          )}
                          {activeProfile.profile.tono_visual && (
                            <div className="bg-muted rounded-lg p-2.5">
                              <div className="flex items-center gap-1 mb-1">
                                <Star className="w-3 h-3 text-muted-foreground/70" />
                                <span className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">Tono</span>
                              </div>
                              <p className="text-xs text-muted-foreground italic truncate">{activeProfile.profile.tono_visual}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Sequence preview - showing 7 active images */}
            <div className="mt-12">
              <h3 className="text-lg font-semibold text-foreground mb-4 text-center">Las 7 infografías que generaremos</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {ACTIVE_SEQUENCE.map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.pos} className="bg-card/60 border border-border rounded-xl p-4 text-center">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${s.color} flex items-center justify-center mx-auto mb-2`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-sm font-medium text-foreground">{s.pos}. {s.name}</p>
                      <p className="text-xs text-muted-foreground/70 mt-1">{s.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════════════ */}
            {/* HISTORIAL DE GENERACIONES (últimas 24 horas) */}
            {/* ══════════════════════════════════════════════════════════════════════════ */}
            <div className="mt-12 border-t border-border pt-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  Mis últimas generaciones
                </h3>
                <button
                  onClick={() => {
                    setShowHistory(!showHistory);
                    if (!showHistory && generationHistory.length === 0) {
                      loadGenerationHistory();
                    }
                  }}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors flex items-center gap-1"
                  data-testid="toggle-history-btn"
                >
                  <FolderOpen className="w-4 h-4" />
                  {showHistory ? 'Ocultar' : 'Ver historial'}
                </button>
              </div>

              {showHistory && (
                <div className="space-y-4">
                  {historyLoading ? (
                    <div className="flex items-center justify-center py-8 text-muted-foreground">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Cargando historial...
                    </div>
                  ) : generationHistory.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground/70">
                      <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No tienes generaciones en las últimas 24 horas</p>
                    </div>
                  ) : (
                    <div className="grid gap-3">
                      {generationHistory.map((item) => (
                        <div
                          key={item.session_id}
                          className="bg-card/60 border border-border rounded-xl p-4 hover:border-blue-500/50 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                <ImageIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div>
                                <p className="font-medium text-foreground">{item.product_name}</p>
                                <p className="text-xs text-muted-foreground/70">
                                  {new Date(item.completed_at).toLocaleString('es-CO')} • {item.success_count}/{item.total_positions} imágenes
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => loadHistoryPreview(item.session_id)}
                                className="px-3 py-1.5 rounded-lg bg-muted text-muted-foreground hover:text-foreground hover:bg-slate-700 transition-all text-sm flex items-center gap-1"
                                data-testid={`preview-history-${item.session_id}`}
                              >
                                <Eye className="w-4 h-4" /> Ver
                              </button>
                              <button
                                onClick={() => handleDownloadZip(item.session_id)}
                                className="px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-all text-sm flex items-center gap-1"
                                data-testid={`download-history-${item.session_id}`}
                              >
                                <Download className="w-4 h-4" /> ZIP
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Modal de preview del historial */}
                  {historyPreview && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                      <div className="bg-popover border border-border rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-border">
                          <h3 className="text-lg font-semibold text-foreground">
                            {historyPreview.product_name}
                          </h3>
                          <button
                            onClick={() => setHistoryPreview(null)}
                            className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {historyPreview.images.map((img) => (
                              <div key={img.position} className="relative group">
                                {img.has_image ? (
                                  <>
                                    <img
                                      src={`data:image/png;base64,${img.image_b64}`}
                                      alt={`Infografía ${img.position}`}
                                      className="w-full aspect-square object-cover rounded-lg"
                                    />
                                    <button
                                      onClick={() => handleDownloadDirect(img.position, historyPreview.session_id)}
                                      className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                                    >
                                      <Download className="w-6 h-6 text-white" />
                                    </button>
                                  </>
                                ) : (
                                  <div className="w-full aspect-square bg-muted rounded-lg flex items-center justify-center">
                                    <X className="w-8 h-8 text-muted-foreground/70" />
                                  </div>
                                )}
                                <p className="text-xs text-center text-muted-foreground mt-1">Pos. {img.position}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="p-4 border-t border-border flex justify-end gap-2">
                          <button
                            onClick={() => setHistoryPreview(null)}
                            className="px-4 py-2 rounded-lg bg-muted text-muted-foreground hover:text-foreground hover:bg-slate-700 transition-all text-sm"
                          >
                            Cerrar
                          </button>
                          <button
                            onClick={() => handleDownloadZip(historyPreview.session_id)}
                            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-all text-sm flex items-center gap-2"
                          >
                            <Download className="w-4 h-4" /> Descargar todas (ZIP)
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Step 1: Analysis ── */}
        {step === 1 && analysis && (
          <div className="space-y-6" data-testid="step-analyze">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold font-outfit text-foreground">Análisis del Producto</h2>
              <div className="flex gap-2">
                <button onClick={() => {
                  if (editingAnalysis) {
                    // Cancelar - restaurar estado original
                    if (originalAnalysis) {
                      setAnalysis(originalAnalysis);
                    }
                    setEditingAnalysis(false);
                    toast.info('Cambios descartados');
                  } else {
                    // Entrar en modo edición - guardar estado original
                    setOriginalAnalysis(JSON.parse(JSON.stringify(analysis)));
                    setEditingAnalysis(true);
                  }
                }} data-testid="toggle-edit-analysis"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted text-muted-foreground hover:text-foreground hover:bg-slate-700 transition-all text-sm">
                  <Edit3 className="w-4 h-4" /> {editingAnalysis ? 'Cancelar' : 'Editar'}
                </button>
                {editingAnalysis && (
                  <button onClick={handleSaveAnalysis} data-testid="save-analysis-btn"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-all text-sm">
                    <Check className="w-4 h-4" /> Guardar
                  </button>
                )}
              </div>
            </div>

            {/* Multiple images indicator */}
            {productPreviews.length > 1 && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <ImageIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">{productPreviews.length} imágenes base cargadas</p>
                  <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70">El Director de Arte seleccionará inteligentemente cada foto para cada posición</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card/60 border border-border rounded-2xl p-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Imagen del producto (sin fondo)</h3>
                {sessionId && (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL || ''}/api/publications/session/${sessionId}/product-image?variant=nobg&token=${localStorage.getItem('authToken')}`}
                    alt="Producto sin fondo"
                    className="w-full max-h-80 object-contain rounded-lg bg-muted"
                    data-testid="product-nobg-preview"
                    onError={(e) => { if (productPreviews[0]) e.target.src = productPreviews[0]; }}
                  />
                )}
                {/* Add more images button */}
                {productPreviews.length < MAX_PRODUCT_IMAGES && (
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-dashed border-border text-muted-foreground hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4" /> Añadir más fotos del producto
                  </button>
                )}
              </div>

              <div className="bg-card/60 border border-border rounded-2xl p-6 space-y-4">
                <AnalysisField label="Nombre" value={analysis.nombre_producto} field="nombre_producto" editing={editingAnalysis} onChange={(v) => setAnalysis(p => ({ ...p, nombre_producto: v }))} />
                <AnalysisField label="Categoría" value={analysis.categoria} field="categoria" editing={editingAnalysis} onChange={(v) => setAnalysis(p => ({ ...p, categoria: v }))} />
                <AnalysisField label="Frase gancho" value={analysis.frase_gancho} field="frase_gancho" editing={editingAnalysis} onChange={(v) => setAnalysis(p => ({ ...p, frase_gancho: v }))} />
                <AnalysisField label="Color principal" value={analysis.color_principal} field="color_principal" editing={editingAnalysis} onChange={(v) => setAnalysis(p => ({ ...p, color_principal: v }))} />
                <AnalysisField label="Material" value={analysis.material} field="material" editing={editingAnalysis} onChange={(v) => setAnalysis(p => ({ ...p, material: v }))} />
                <AnalysisField label="Uso principal" value={analysis.uso_principal} field="uso_principal" editing={editingAnalysis} onChange={(v) => setAnalysis(p => ({ ...p, uso_principal: v }))} />
                <AnalysisField label="Estilo" value={analysis.estilo_sugerido} field="estilo_sugerido" editing={editingAnalysis} onChange={(v) => setAnalysis(p => ({ ...p, estilo_sugerido: v }))} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <AnalysisListField label="Características" items={analysis.caracteristicas || []} editing={editingAnalysis} onChange={(v) => setAnalysis(p => ({ ...p, caracteristicas: v }))} />
              <AnalysisListField label="Beneficios" items={analysis.beneficios || []} editing={editingAnalysis} onChange={(v) => setAnalysis(p => ({ ...p, beneficios: v }))} />
              <AnalysisListField label="Diferenciadores" items={analysis.diferenciadores || []} editing={editingAnalysis} onChange={(v) => setAnalysis(p => ({ ...p, diferenciadores: v }))} />
              <AnalysisListField label="Objeciones comunes" items={analysis.objeciones_comunes || []} editing={editingAnalysis} onChange={(v) => setAnalysis(p => ({ ...p, objeciones_comunes: v }))} />
            </div>

            {/* Botón de generación */}
            <div className="flex justify-center pt-4">
              <button onClick={handleGenerateAll} data-testid="generate-all-btn"
                className="flex items-center gap-3 px-8 py-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold text-lg
                  hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-500/30 transition-all transform hover:-translate-y-0.5">
                <Sparkles className="w-6 h-6" /> Generar 7 Infografías
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Generating ── */}
        {step === 2 && (
          <div className="space-y-8" data-testid="step-generating">
            <div className="text-center space-y-3">
              <h2 className="text-2xl font-bold font-outfit text-foreground">Generando Infografías</h2>
              <p className="text-muted-foreground">
                Creando tus 7 infografías de alto impacto...
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {ACTIVE_SEQUENCE.map((s) => {
                const Icon = s.icon;
                const generated = generatedImages.find(g => g.position === s.pos);
                const isGen = currentGenPos === s.pos;
                const isPending = ACTIVE_POSITIONS.indexOf(s.pos) > ACTIVE_POSITIONS.indexOf(currentGenPos);
                return (
                  <div key={s.pos} className={`relative rounded-xl border p-4 text-center transition-all
                    ${isGen ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20' : ''}
                    ${generated?.success ? 'border-emerald-500/50 bg-emerald-500/5' : ''}
                    ${generated && !generated.success ? 'border-rose-500/50 bg-rose-500/5' : ''}
                    ${!isGen && !generated ? 'border-border bg-popover/30' : ''}
                  `}>
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${s.color} flex items-center justify-center mx-auto mb-2 ${isPending ? 'opacity-30' : ''}`}>
                      {isGen ? <Loader2 className="w-5 h-5 text-white animate-spin" /> :
                       generated?.success ? <Check className="w-5 h-5 text-white" /> :
                       generated && !generated.success ? <X className="w-5 h-5 text-white" /> :
                       <Icon className="w-5 h-5 text-white" />}
                    </div>
                    <p className={`text-xs font-medium ${isPending ? 'text-muted-foreground/70' : 'text-muted-foreground'}`}>{s.pos}. {s.name}</p>
                    {isGen && <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Generando IA...</p>}
                  </div>
                );
              })}
            </div>

            <div className="max-w-md mx-auto">
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-gradient-to-r from-blue-600 to-cyan-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${(generatedImages.length / 7) * 100}%` }} />
              </div>
              <p className="text-center text-sm text-muted-foreground mt-2">{generatedImages.length} de 7 imágenes</p>
            </div>

            {generatedImages.filter(g => g.success).length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {generatedImages.filter(g => g.success).map((img) => (
                  <div key={img.position} className="bg-card/60 border border-border rounded-xl overflow-hidden">
                    <img src={getImageUrl(img.position, img.generated_at)} alt={img.name} className="w-full aspect-square object-cover" />
                    <p className="text-xs text-center text-muted-foreground py-2">{img.position}. {img.name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Step 3: Results ── */}
        {step === 3 && (
          <div className="space-y-6" data-testid="step-results">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold font-outfit text-foreground">7 Infografías Generadas</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Listas para usar en tu publicación
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={handleDownloadAll} data-testid="download-all-btn"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 transition-all text-sm">
                  <Download className="w-4 h-4" /> Descargar Todas
                </button>
                <button onClick={handleReset} data-testid="new-session-btn"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted text-muted-foreground hover:text-foreground hover:bg-slate-700 transition-all text-sm">
                  <Upload className="w-4 h-4" /> Nuevo Producto
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {ACTIVE_SEQUENCE.map((s) => {
                const img = generatedImages.find(g => g.position === s.pos);
                const Icon = s.icon;
                return (
                  <div key={s.pos} className="bg-card/60 border border-border rounded-2xl overflow-hidden group hover:border-border transition-all" data-testid={`result-card-${s.pos}`}>
                    <div className="relative aspect-square bg-muted">
                      {img?.success ? (
                        <>
                          <img src={getImageUrl(s.pos, img?.generated_at)} alt={s.name} className="w-full h-full object-cover" data-testid={`generated-image-${s.pos}`} />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                            <button onClick={() => handleDownload(s.pos)} data-testid={`download-btn-${s.pos}`}
                              className="p-2.5 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors" title="Descargar">
                              <Download className="w-5 h-5" />
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-muted-foreground/70">
                          {img ? (
                            <>
                              <AlertCircle className="w-8 h-8 text-rose-600 dark:text-rose-400" />
                              <p className="text-xs text-rose-600 dark:text-rose-400 px-2 text-center">
                                {img.error_type === 'ai_unavailable' || (img.error && img.error.includes('IA no disponible'))
                                  ? 'IA no disponible'
                                  : 'Error al generar'}
                              </p>
                              <button onClick={() => handleRegenerate(s.pos)} className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1" data-testid={`retry-btn-${s.pos}`}>
                                <RefreshCw className="w-3 h-3" /> Reintentar
                              </button>
                            </>
                          ) : (
                            <><Icon className="w-8 h-8" /><p className="text-xs">No generada</p></>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${s.color} flex items-center justify-center`}>
                          <span className="text-xs font-bold text-white">{s.pos}</span>
                        </div>
                        <span className="text-sm font-medium text-foreground">{s.name}</span>
                        {img?.success && <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 ml-auto" />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bg-card/60 border border-border rounded-xl p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {generatedImages.filter(g => g.success).length} de 7 imágenes generadas exitosamente
                    </p>
                    <p className="text-xs text-muted-foreground">Tus imágenes estarán disponibles por 24 horas en el historial</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => handleDownloadZip()}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 transition-all text-sm"
                    data-testid="download-zip-btn"
                  >
                    <Download className="w-4 h-4" /> Descargar ZIP
                  </button>
                </div>
              </div>
              
              {/* Fallback: Enlaces individuales */}
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground/70 mb-2">¿Problemas con la descarga? Usa estos enlaces individuales:</p>
                <div className="flex flex-wrap gap-2">
                  {generatedImages.filter(g => g.success).map((img) => (
                    <button
                      key={img.position}
                      onClick={() => handleDownloadDirect(img.position)}
                      className="px-2 py-1 rounded bg-muted text-muted-foreground hover:text-foreground hover:bg-slate-700 transition-colors text-xs flex items-center gap-1"
                      data-testid={`fallback-download-${img.position}`}
                    >
                      <Download className="w-3 h-3" /> Imagen {img.position}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
          </TabsContent>

          {/* Tab: Optimizador SEO */}
          <TabsContent value="seo" className="mt-0">
            <SeoOptimizerTab onAnalysisComplete={setSeoContext} />
          </TabsContent>

          {/* Tab: Generador de Descripción */}
          <TabsContent value="description" className="mt-0">
            <DescriptionGeneratorTab 
              seoContext={seoContext} 
              onNavigateToSeo={() => setActiveTab('seo')} 
            />
          </TabsContent>
        </Tabs>
      </main>

      <BuyCreditsModal
        open={buyCreditsOpen}
        onOpenChange={setBuyCreditsOpen}
        currentBalance={imageCredits?.purchased_balance || 0}
      />
    </div>
  );
};

// ── Helper components ──

const AnalysisField = ({ label, value, field, editing, onChange }) => (
  <div>
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {editing ? (
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 px-3 py-2 rounded-lg bg-muted border border-border text-foreground text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
        data-testid={`analysis-input-${field}`} />
    ) : (
      <p className="text-sm text-foreground mt-1">{value || '—'}</p>
    )}
  </div>
);

const AnalysisListField = ({ label, items, editing, onChange }) => (
  <div className="bg-card/60 border border-border rounded-2xl p-4">
    <label className="text-xs font-medium text-muted-foreground block mb-2">{label}</label>
    {editing ? (
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <input type="text" value={item}
              onChange={(e) => { const c = [...items]; c[i] = e.target.value; onChange(c); }}
              className="flex-1 px-3 py-1.5 rounded-lg bg-muted border border-border text-foreground text-sm focus:border-blue-500 outline-none" />
            <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button onClick={() => onChange([...items, ''])} className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">+ Agregar</button>
      </div>
    ) : (
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
            <ChevronRight className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" /> {item}
          </li>
        ))}
        {items.length === 0 && <p className="text-sm text-muted-foreground/70">Sin datos</p>}
      </ul>
    )}
  </div>
);

export default Publications;
